import { ColumnDetails } from '../../../grid/types/common';
import { html, TemplateResult } from 'lit';
import { translateFn } from '../../../utils/translator.helper';
import { GzSelectWebComponent } from '../../../ui/gzSelect';
import { SelectEvents, SelectOption } from '../../../ui/gzSelect/types';
import { Column, ColumnKeys, PhasrRecommendationsGridEvents } from '../types/grid';
import { PhasrRenderIconSet } from '../../uiUtils/iconSet';
import { PhasrIcons } from '../../uiUtils/types';
import { ButtonColor, ButtonSize } from '../../../ui/gzIconButton/types';
import {
  ActionTakenTypes, getPhasrCategoryDictionaryToPrefferedName,
} from '../../../types/common/phasrTypes';
import { RowDataGrid } from '../types/phasr-recommendations-grid-entry';
import { dateFormatter } from '@nirvana/shared/time';
import { PhasrApplyActionSchema } from '../../../phasrApplyActionPanel/types';
import { ALLOW_ACTION, RESTRICT_ACTION } from '../types';

export function renderBehavioralProfiles(rowData: RowDataGrid, columnDetails: ColumnDetails<Column, ColumnKeys>): TemplateResult {
  const behavioralProfilesCount = rowData.item[columnDetails.key];
  let customSpanStyle: string;

  if (!behavioralProfilesCount) {
    customSpanStyle = `
      color: var(--chart-302-icon-buttons-titles);
      padding-left: 22px;
    `;
    return html`
      <span class='cell-label' part='label' style='${customSpanStyle}'>
          --
      </span>
    `
  }

  customSpanStyle = `
    color: var(--body-text-arrows-400);
    padding-left: 3px;
  `;
  const customClassSvg = `phasr-behavioral-svg-${rowData.index}`;
  const customTooltipStyle = `
        flex-basis: fit-content
  `;
  const customClassLabel = `phasr-behavioral-label-${rowData.index}`;
  const iconText = behavioralProfilesCount < 10000 ? behavioralProfilesCount : '9999+';

  return html`
    <gz-tooltip class='list-buttons' part='item-group-icons' style='${customTooltipStyle}'
                tooltipText='${behavioralProfilesCount >= 100 ? behavioralProfilesCount : ''}'>
      <gz-button
        @click='${() => this.handleBehavioralProfilesClick(this.dom, customClassLabel, customClassSvg, rowData.item)}'
        type='quiet'
        color=''
        buttonheight='medium'
        buttonfontsize='small'
        part='button'
        style='margin-left: -16px'
      >
        ${PhasrRenderIconSet.createIcon(PhasrIcons.BEHAVIORAL_PROFILES_ICON, ButtonColor.SECONDARY, ButtonSize.SIZE_24, `${customClassSvg}`)}
        <span class='label ${customClassLabel}' part='label' style='${customSpanStyle}'>
          ${iconText}
        </span>
      </gz-button>
    </gz-tooltip>
  `;
}

/**
 * Used to render the columns that depend on their ruleId property to translate and render data
 *
 * Columns:
 * - Name
 * - Description
 * - Monitored Rule
 */
export function renderRuleIdDependentColumns(rowData: RowDataGrid, columnDetails: ColumnDetails<Column, ColumnKeys>): TemplateResult {
  let value = 'N/A'
  let field = '';

  switch (columnDetails.column) {
    case Column.RECOMMENDATION_NAME:
      const id = String(rowData.item[ColumnKeys.RECOMMENDATION_NAME]);
      field = rowData.item.type === RESTRICT_ACTION ? 'rule_recommendation' : 'rule_allow_name';
      value = this.dictionary[String(rowData.item[ColumnKeys.RECOMMENDATION_NAME])]?.[field] ?? 'N/A';
      return html`
        <gz-link class='cell-label' type="quiet" color="primary"
                 @click=${() => this.handleRecommendationNameClick(this.dom, rowData.item, this.dictionary[id] ?? null)}>
          ${value}
        </gz-link>
      `;
    case Column.DESCRIPTION:
      value = this.dictionary[String(rowData.item[ColumnKeys.RECOMMENDATION_NAME])]?.['rule_description'] ?? 'N/A';
      return html`
        <span class='cell-label' style='white-space: nowrap; overflow: hidden; text-overflow: ellipsis;'>
            ${value}
          </span>
      `;

    case Column.MONITORED_RULE:
      value = this.dictionary[String(rowData.item[ColumnKeys.RECOMMENDATION_NAME])]?.['rule_name'] ?? 'N/A';
      return html`
        <gz-link class='cell-label' type="quiet" color="primary" @click=${() => this.handleRuleNameClick(rowData.item)}>
          ${value}
        </gz-link>
      `;
  }

  return html`<span class='cell-label'>${value}</span>`;
}

export function renderTargetActivityType(rowData: RowDataGrid): TemplateResult {
  let value = String(rowData.item[ColumnKeys.RECOMMENDATION_NAME]);

  value = getPhasrCategoryDictionaryToPrefferedName()[this.dictionary[value]?.['category']] ?? 'N/A';
  return html`<span class='cell-label'>${value}</span>`;
}

/**
 *
 * Received rowData and accesses the createdOn field that is received as a UNIX timestamp
 * Handles conversion to milliseconds for moment date parsing
 *
 */
export function createdOnRenderer(rowData: RowDataGrid, columnDetails: ColumnDetails<Column, ColumnKeys>): TemplateResult {
  let data = 'N/A';
  if (rowData.item[columnDetails.key]) {
    const timezone = this.dependencies.getTimezone();
    data = dateFormatter(rowData.item[columnDetails.key] * 1000, timezone, 'DD MMM YYYY, HH:mm')
  }

  return html`<span class='cell-label'>${data}</span>`;
}

export function renderAttackSurfaceExposure(rowData: RowDataGrid, columnDetails: ColumnDetails<Column, ColumnKeys>): TemplateResult {
  let value = rowData.item.type === RESTRICT_ACTION ? `${rowData.item[columnDetails.key]}%` : '-';
  const customSpanStyle = `
    color: var(--green-layout-100-primary-green)
  `;
  if (rowData.item[columnDetails.key] !== 0) {
    value = `-${value}`;
  }
  return html`<span class='cell-label'
                    style='${rowData.item.type === RESTRICT_ACTION ? customSpanStyle : ''}'>${value}</span>`;
}

export function companyRenderer(
  rowData: RowDataGrid,
  columnDetails: ColumnDetails<Column, ColumnKeys>
): TemplateResult {
  const companyName = rowData.item[columnDetails.key];
  if (companyName) {
    return html`<span class='cell-label'>${companyName}</span>`;
  }
  return translateFn('N/A');
}

/**
 * Render row action buttons
 *
 * @param rowData
 * @private
 */
export function actionButtonsRenderer(rowData: RowDataGrid): HTMLElement | void {
  const recommendation = rowData.item;
  const options: Array<SelectOption> = [];

  if (recommendation.type === ALLOW_ACTION) {
    options.push({
      title: translateFn('Allow access'),
      value: PhasrRecommendationsGridEvents.OnRecommendationAllowAccess,
      id: recommendation.objectId,
      disabled: recommendation.actionTaken !== ActionTakenTypes.ACTION_NEEDED || !recommendation.phasrUsable,
    });
  } else {
    options.push({
      title: translateFn('Restrict access'),
      value: PhasrRecommendationsGridEvents.OnRecommendationRestrictAccess,
      id: recommendation.objectId,
      disabled: recommendation.actionTaken !== ActionTakenTypes.ACTION_NEEDED || !recommendation.phasrUsable,
    });
  }

  const gzSelect = new GzSelectWebComponent();
  const cellContainer: HTMLElement = document.createElement('div');
  cellContainer.className = 'd-flex justify-end w-full';

  gzSelect.options = options;
  gzSelect.innerHTML = `<gz-icon-button
          icon='ds-icon-more'
          fontSize='24'
          buttonType='square'
          buttonFill='quiet'
          buttonColor='secondary'>
     </gz-icon-button>`;

  gzSelect.addEventListener(SelectEvents.SELECTION_CHANGED, (e: CustomEvent) => {
    const selectedOption = e.detail.selected;

    const data: PhasrApplyActionSchema = {
      id: recommendation.objectId,
      companyId: recommendation.companyId,
      ruleId: recommendation.ruleId,
      reductionPotential: recommendation.attackSurfaceReduction,
      description: this.dictionary[recommendation.ruleId]?.rule_description ?? 'N/A',
      targetedActivityType: getPhasrCategoryDictionaryToPrefferedName()[this.dictionary[recommendation.ruleId]?.category] ?? 'N/A',
      rule: this.dictionary[recommendation.ruleId]?.rule_name ?? 'N/A',
      type: recommendation.type,
      createdOn: recommendation.createdOn,
    };

    const event = new CustomEvent(selectedOption, {
      detail: data,
      bubbles: true,
      composed: true,
    });

    this.dom.$grid.dispatchEvent(event);
  });

  cellContainer.appendChild(gzSelect);

  return cellContainer;
}
